import { Component, inject, signal, input, output, effect, CUSTOM_ELEMENTS_SCHEMA, resource, computed } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { MembershipService } from '../../../core/services/membership.service';
import { AppModal } from '../app-modal/app-modal';
import { AppButton } from '../app-button/app-button';
import { LoadingService } from '../../../core/services/loading.service';
import { ToastService } from '../../../core/services/toast.service';
import { AppSearchPicker } from '../app-search-picker/app-search-picker';

@Component({
  selector: 'app-membership-assign-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AppModal, AppButton, AppSearchPicker],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <app-modal [isOpen]="isOpen()" (isOpenChange)="close()" 
               [title]="(businessId() ? 'Membresía' : 'Asignar Membresía')"
               icon="solar:card-2-bold-duotone">
      
      <div header-content *ngIf="businessId()" class="px-1 -mt-1 mb-4">
        <h2 class="text-sm font-black text-primary uppercase tracking-tight truncate">
            {{ businessName() || 'Cargando...' }}
        </h2>
      </div>

      <form [formGroup]="form" (ngSubmit)="save()" class="space-y-4 pt-0">
        <div *ngIf="!businessId()" class="p-3 bg-primary/5 rounded-xl border border-primary/10 mb-2">
            <app-search-picker 
                type="business" 
                label="Seleccionar Clínica"
                placeholder="Buscar..."
                icon="solar:buildings-bold-duotone"
                (selected)="onBusinessSelected($event)">
            </app-search-picker>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
          <!-- Plan Type -->
          <div class="flex flex-col gap-1">
            <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                <iconify-icon icon="solar:medal-star-bold" width="10"></iconify-icon>
                Tipo de Plan
            </label>
            <select formControlName="planType" 
                    class="w-full h-10 px-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-bold focus:ring-2 focus:ring-primary/20 outline-none transition-all cursor-pointer">
              <option *ngFor="let plan of plans()" [value]="plan.code">
                {{ plan.name }}
              </option>
            </select>
          </div>

          <!-- Billing Cycle -->
          <div class="flex flex-col gap-1">
            <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                <iconify-icon icon="solar:calendar-minimalistic-bold" width="10"></iconify-icon>
                Ciclo de Cobro
            </label>
            <select formControlName="billingCycle" 
                    class="w-full h-10 px-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-bold focus:ring-2 focus:ring-primary/20 outline-none transition-all cursor-pointer">
              <option value="MONTHLY">Mensual</option>
              <option value="ANNUAL">Anual</option>
            </select>
          </div>

          <!-- Status -->
          <div class="flex flex-col gap-1">
            <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                <iconify-icon icon="solar:shield-check-bold" width="10"></iconify-icon>
                Estado
            </label>
            <select formControlName="status" 
                    class="w-full h-10 px-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-bold focus:ring-2 focus:ring-primary/20 outline-none transition-all cursor-pointer">
              <option value="ACTIVE">Activa</option>
              <option value="PENDING_EXPIRATION">Próxima a Vencer</option>
              <option value="EXPIRED">Expirada</option>
              <option value="BLOCKED">Bloqueada</option>
            </select>
          </div>

          <!-- Start Date -->
          <div class="flex flex-col gap-1">
            <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                <iconify-icon icon="solar:calendar-add-bold" width="10"></iconify-icon>
                Fecha de Inicio
            </label>
            <input type="date" formControlName="startDate" 
                   class="w-full h-10 px-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-bold focus:ring-2 focus:ring-primary/20 outline-none transition-all" />
          </div>

          <!-- Expiration Date -->
          <div class="flex flex-col gap-1">
            <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                <iconify-icon icon="solar:calendar-mark-bold" width="10"></iconify-icon>
                Fecha de Fin
            </label>
            <input type="date" formControlName="endDate" 
                   class="w-full h-10 px-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-bold focus:ring-2 focus:ring-primary/20 outline-none transition-all" />
          </div>

          <!-- Amount Paid -->
          <div class="flex flex-col gap-1">
            <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                <iconify-icon icon="solar:wad-of-money-bold" width="10"></iconify-icon>
                Monto Cobrado (USD)
            </label>
            <input type="number" formControlName="amountPaid" 
                   class="w-full h-10 px-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-bold focus:ring-2 focus:ring-primary/20 outline-none transition-all font-mono" />
          </div>

          <!-- Payment Status -->
          <div class="md:col-span-2 flex flex-col gap-1">
            <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                <iconify-icon icon="solar:bill-check-bold" width="10"></iconify-icon>
                Estado de Pago
            </label>
            <select formControlName="paymentStatus" 
                    class="w-full h-10 px-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-bold focus:ring-2 focus:ring-primary/20 outline-none transition-all cursor-pointer">
              <option value="SUCCESS">Cobrado</option>
              <option value="PENDING">Pendiente</option>
            </select>
          </div>
        </div>

        <div class="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-100 dark:border-amber-800 flex gap-2 text-amber-700 dark:text-amber-400">
          <iconify-icon icon="solar:info-circle-bold-duotone" width="16"></iconify-icon>
          <p class="text-[10px] font-bold leading-normal">
            Esta acción registrará un pago manual y actualizará el acceso de forma inmediata.
          </p>
        </div>
      </form>

      <div actions class="flex justify-end gap-2 w-full">
        <app-button variant="glass" size="xs" (btnClick)="close()">Cancelar</app-button>
        <app-button variant="primary" size="xs" [disabled]="form.invalid || loading()" (btnClick)="save()">
          {{ loading() ? 'Guardando...' : 'Asignar Membresía' }}
        </app-button>
      </div>
    </app-modal>
  `
})
export class MembershipAssignDialog {
  private fb = inject(FormBuilder);
  private membershipService = inject(MembershipService);
  private loadingService = inject(LoadingService);
  private toast = inject(ToastService);
  
  selectedBusiness = signal<any>(null);

  isOpen = input<boolean>(false);
  businessId = input<string>('');
  businessName = input<string>('');
  onClose = output<void>();
  onSave = output<any>();

  form = this.fb.group({
    planType: ['BASIC', Validators.required],
    billingCycle: ['MONTHLY', Validators.required],
    status: ['ACTIVE', Validators.required],
    startDate: [new Date().toISOString().split('T')[0], Validators.required],
    endDate: ['', Validators.required],
    amountPaid: [0, [Validators.required, Validators.min(0)]],
    paymentStatus: ['SUCCESS', Validators.required]
  });

  loading = signal(false);

  // Cargar planes dinámicamente
  plansResource = resource({
    loader: () => lastValueFrom(this.membershipService.getPlans())
  });
  plans = computed(() => this.plansResource.value() || []);

  constructor() {
    effect(() => {
      const id = this.businessId();
      if (id && this.isOpen()) {
        this.loadCurrentMembership(id);
      }
    });

    // Escuchar cambios en el formulario para auto-llenar datos
    this.form.valueChanges.subscribe(() => {
        this.calculateAutoFields();
    });

    // También disparar cuando los planes terminen de cargar
    effect(() => {
        if (this.plans().length > 0) {
            this.calculateAutoFields();
        }
    });
  }

  private calculateAutoFields() {
    const planCode = this.form.get('planType')?.value;
    const cycle = this.form.get('billingCycle')?.value;
    const startDateVal = this.form.get('startDate')?.value;
    const plans = this.plans();
    const selected = plans.find((p: any) => p.code === planCode);
    
    if (selected) {
        // Actualizar precio si no se ha tocado manualmente
        if (this.form.get('amountPaid')?.pristine) {
            let price = selected.monthlyPrice || 0;
            if (cycle === 'ANNUAL') {
                const discount = selected.annualDiscountPercentage || 0;
                price = (price * 12) * (1 - (discount / 100));
            }
            this.form.patchValue({ amountPaid: Number(price.toFixed(2)) }, { emitEvent: false });
        }

        // Calcular fecha de fin automática
        if (startDateVal) {
            const start = new Date(startDateVal + 'T12:00:00');
            const end = new Date(start);
            
            if (cycle === 'ANNUAL') {
                end.setFullYear(start.getFullYear() + 1);
            } else {
                end.setMonth(start.getMonth() + 1);
            }

            // Solo actualizar si es diferente para evitar loops
            const currentEnd = this.form.get('endDate')?.value;
            const newEndStr = end.toISOString().split('T')[0];
            if (currentEnd !== newEndStr) {
                this.form.patchValue({ endDate: newEndStr }, { emitEvent: false });
            }
        }
    }
  }

  loadCurrentMembership(id: string) {
    this.membershipService.getAdminStatus(id).subscribe(m => {
      if (m) {
        this.form.patchValue({
          planType: m.planType || 'BASIC',
          billingCycle: m.billingCycle || 'MONTHLY',
          status: m.status || 'ACTIVE',
          startDate: m.startDate ? new Date(m.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          endDate: m.endDate ? new Date(m.endDate).toISOString().split('T')[0] : '',
          amountPaid: m.amountPaid || 0
        });
      } else {
        this.form.reset({
            planType: 'BASIC',
            billingCycle: 'MONTHLY',
            status: 'ACTIVE',
            startDate: new Date().toISOString().split('T')[0],
            endDate: '',
            amountPaid: 0,
            paymentStatus: 'SUCCESS'
        });
      }
    });
  }

  onBusinessSelected(business: any) {
    this.selectedBusiness.set(business);
    if (business?._id) {
        this.loadCurrentMembership(business._id);
    }
  }

  close() {
    this.onClose.emit();
  }

  save() {
    if (this.form.invalid) return;

    this.loading.set(true);
    this.loadingService.showLoading('Guardando', 'Asignando membresía...');

    const val = this.form.value;
    const bizId = this.businessId() || this.selectedBusiness()?._id;

    if (!bizId) {
      this.toast.show('Debes seleccionar un negocio primero', 'warning');
      this.loading.set(false);
      this.loadingService.hideLoading();
      return;
    }

    this.membershipService.adminAssign({
      businessId: bizId,
      planType: val.planType!,
      billingCycle: val.billingCycle!,
      status: val.status!,
      startDate: val.startDate! ? new Date(val.startDate!) : undefined,
      endDate: new Date(val.endDate!),
      amountPaid: val.amountPaid!,
      paymentStatus: val.paymentStatus!
    }).subscribe({
      next: (res) => {
        this.toast.show('Membresía asignada correctamente', 'success');
        this.onSave.emit(res);
        this.close();
        this.loading.set(false);
        this.loadingService.hideLoading();
      },
      error: () => {
        this.loading.set(false);
        this.loadingService.hideLoading();
      }
    });
  }
}
